// reveal.plugin.js
//
// Vue-директива `v-reveal` — fade-in/stagger/word-reveal анимации появления
// при скролле, без GSAP: IntersectionObserver + инлайн CSS-transition.
//
//   v-reveal                                     — обычный fade-in
//   v-reveal="{ type: 'fade' }"                   — то же явно
//   v-reveal="{ type: 'stagger', stagger: 80 }"   — контейнер: непосредственные
//                                                    дети появляются по очереди
//   v-reveal="{ type: 'words', stagger: 30 }"     — статичный текстовый узел:
//                                                    появляется по словам
//   v-reveal="{ type: 'line' }"                   — divider: ширина 0 → 100%
//
// Опции: stagger (ms), delay (ms, база перед стартом), once (bool, default true),
// threshold, rootMargin — пробрасываются в IntersectionObserver.
//
// Анимация задаётся ИНЛАЙН-стилями (el.style.*), а не CSS-классами: любой
// целевой элемент дизайн-системы/темы проекта (.card, .button, .feature-card…)
// почти наверняка уже объявляет свой `transition` — по правилам каскада
// (@layer / unlayered-тема) он перебивает transition-delay из класса, стаггер
// молча обнуляется. Инлайн-стиль сильнее любого класса независимо от слоя,
// поэтому анимация гарантированно не ломается чужим CSS.
//
// type:'words' мутирует innerHTML (оборачивает слова в span) — применять только
// к статичным текстовым узлам, не к элементам с реактивным контентом внутри.

const STAGGER_DEFAULTS = { fade: 80, stagger: 80, words: 30, line: 0 };
const EASE = 'cubic-bezier(.34,1.56,.64,1)'; // back.out(1.4)-эквивалент
const EASE_LINE = 'cubic-bezier(.25,.46,.45,.94)'; // power2.out-эквивалент

// Общий registry: один IntersectionObserver на комбинацию threshold+rootMargin,
// а не по экземпляру на каждый reveal-элемент.
const observers = new Map();
const callbacks = new WeakMap();

function getObserver(threshold, rootMargin) {
  const key = `${threshold}|${rootMargin}`;

  if (!observers.has(key)) {
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        const cb = callbacks.get(entry.target);
        if (!cb) return;

        if (entry.isIntersecting) {
          cb.show();
          if (cb.once) obs.unobserve(entry.target);
        } else if (!cb.once) {
          cb.hide();
        }
      });
    }, { root: null, threshold, rootMargin });

    observers.set(key, io);
  }

  return observers.get(key);
}

function observeEl(el, cb, { threshold, rootMargin }) {
  callbacks.set(el, cb);
  getObserver(threshold, rootMargin).observe(el);
}

function unobserveEl(el) {
  callbacks.delete(el);
  observers.forEach((io) => io.unobserve(el));
}

function prefersReducedMotion() {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isMobileViewport() {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 768px)').matches;
}

function normalizeOptions(raw) {
  const o = (raw && typeof raw === 'object') ? raw : {};
  const type = o.type || 'fade';

  return {
    type,
    stagger: o.stagger != null ? o.stagger : (STAGGER_DEFAULTS[type] ?? 80),
    delay: o.delay || 0,
    once: o.once !== false,
    threshold: o.threshold != null ? o.threshold : 0.1,
    rootMargin: o.rootMargin || '0px 0px -10% 0px',
  };
}

// Начальное («скрытое») состояние — ставится сразу в mounted(), задолго до
// триггера, чтобы браузер успел закоммитить его перед стартом transition.
function hideEl(el, opts) {
  el.classList.add('reveal-init');
  if (opts.type === 'line') {
    el.style.overflow = 'hidden';
    el.style.width = '0';
  } else {
    el.style.opacity = '0';
    el.style.transform = 'translateY(50px)';
  }
}

function revealOne(el, opts, delayMs) {
  setTimeout(() => {
    requestAnimationFrame(() => {
      const duration = isMobileViewport() ? '.3s' : '.5s';
      if (opts.type === 'line') {
        el.style.transition = `width ${duration} ${EASE_LINE}, opacity ${duration} ${EASE_LINE}`;
        el.style.opacity = '1';
        el.style.width = '100%';
      } else {
        el.style.transition = `opacity ${duration} ${EASE}, transform ${duration} ${EASE}`;
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
      }
      el.classList.add('is-visible');
    });
  }, delayMs);
}

function hideOne(el, opts) {
  el.classList.remove('is-visible');
  if (opts.type === 'line') {
    el.style.opacity = '0';
    el.style.width = '0';
  } else {
    el.style.opacity = '0';
    el.style.transform = 'translateY(50px)';
  }
}

function prepareElement(el, opts) {
  hideEl(el, opts);
}

function prepareStaggerChildren(el, opts) {
  Array.from(el.children).forEach((child) => hideEl(child, opts));
}

function prepareWordSplit(el, opts) {
  if (el.dataset.revealSplit) return;

  const words = el.textContent.trim().split(/\s+/);
  el.innerHTML = words
    .map((word) => `<span class="reveal-word">${word}</span>`)
    .join(' ');
  el.dataset.revealSplit = 'true';

  Array.from(el.children).forEach((child) => hideEl(child, opts));
}

function triggerReveal(el, opts) {
  const scale = isMobileViewport() ? 0.6 : 1;

  revealOne(el, opts, opts.delay);

  if (opts.type === 'stagger' || opts.type === 'words') {
    Array.from(el.children).forEach((child, i) => {
      revealOne(child, opts, opts.delay + i * opts.stagger * scale);
    });
  }
}

function untriggerReveal(el, opts) {
  hideOne(el, opts);

  if (opts.type === 'stagger' || opts.type === 'words') {
    Array.from(el.children).forEach((child) => hideOne(child, opts));
  }
}

const revealDirective = {
  mounted(el, binding) {
    if (typeof window === 'undefined') return; // SSR: директива no-op на сервере

    const opts = normalizeOptions(binding.value);

    if (prefersReducedMotion()) return; // элемент остаётся как есть, без анимации

    prepareElement(el, opts);
    if (opts.type === 'stagger') prepareStaggerChildren(el, opts);
    if (opts.type === 'words') prepareWordSplit(el, opts);

    observeEl(el, {
      once: opts.once,
      show: () => triggerReveal(el, opts),
      hide: () => untriggerReveal(el, opts),
    }, opts);
  },

  unmounted(el) {
    if (typeof window === 'undefined') return;
    unobserveEl(el);
  },
};

export default {
  install(app) {
    app.directive('reveal', revealDirective);
  },
};

export { revealDirective };
